/**
 * Analytics — Safe event tracking wrapper for PostHog.
 *
 * Wraps posthog.capture() so callers never crash if PostHog is blocked
 * by ad blockers, hasn't initialized, or the API key is missing.
 *
 * Usage:
 *   import { track } from "@/lib/analytics";
 *   track("event_name", { key: "value" });
 *
 * ── Canonical Funnel (PostHog) ──────────────────────────────────────
 *
 *   1. address_entered          — User submits an address (any entry point)
 *      (homepage_address_submitted is a supplementary page-specific event
 *       with source context — direct property identifiers are filtered)
 *   2. fairness_score_viewed    — Dashboard renders with Fairness Score
 *   3. wizard_step_completed    — Each evidence-wizard step (step 1-5)
 *   4. checkout_started         — User clicks "Prepare My Packet"
 *   5. payment_completed        — Stripe confirms payment
 *   6. packet_downloaded        — User downloads the PDF
 *
 * Every event above has ONE canonical call site. Do not duplicate.
 * `property_code_landed` is optional compatibility telemetry for an
 * already-issued or individually prepared property-code link.
 * ────────────────────────────────────────────────────────────────────
 */

import { captureException } from "./sentryLazy";

import { postHogIfLoaded } from "./posthog";
import {
  isBrowserAnalyticsCaptureAllowed,
  isBrowserAnalyticsEnvironmentAllowed,
  withAnalyticsEnvironmentContext,
} from "./analyticsContext";
import {
  safeBrowserTelemetryDistinctId,
  sanitizeTelemetryValue,
} from "./telemetryPrivacy";

import { getAnalyticsAudience, getAnalyticsConsentRevision, hasAcceptedAnalyticsConsent, subscribeAnalyticsPermission } from "./analyticsAudience";

const DIRECT_POSTHOG_ENDPOINT = "https://us.i.posthog.com/capture/";
const MAX_PENDING_EVENTS = 50;
const TRACK_ONCE_STORAGE_PREFIX = "fairpath_analytics_once:";

type AnalyticsProps = Record<string, unknown>;

export type AnalyticsTransportOptions = {
  transport?: "immediate";
};

type PendingEvent = {
  event: string;
  props?: AnalyticsProps;
  dedupeStorageKey?: string;
};

export function sanitizeAnalyticsValue(
  value: unknown,
  key = "",
): unknown {
  return sanitizeTelemetryValue(value, key);
}

function sanitizeAnalyticsProps(
  props?: AnalyticsProps,
): AnalyticsProps | undefined {
  return props
    ? sanitizeAnalyticsValue(props) as AnalyticsProps
    : undefined;
}

let observedWindow: Window | undefined;
let unsubscribePermission: (() => void) | undefined;
const pendingEvents: PendingEvent[] = [];
const trackedOnceKeys = new Set<string>();

function isPostHogReady() {
  if (postHogIfLoaded()) return true;
  if (typeof window === "undefined") return false;
  return Boolean(
    (window as typeof window & { __FAIRPATH_POSTHOG_READY__?: boolean })
      .__FAIRPATH_POSTHOG_READY__
  );
}

/** Return the current pseudonymous identity for server-side event continuity. */
export function getAnalyticsDistinctId(): string | null {
  if (!isBrowserAnalyticsCaptureAllowed()) return null;
  if (!isPostHogReady()) return null;
  try {
    const value = postHogIfLoaded()?.get_distinct_id();
    return typeof value === "string" && value.length >= 8 && value.length <= 200
      ? safeBrowserTelemetryDistinctId(value)
      : null;
  } catch {
    return null;
  }
}

function discardPendingEvent(pending: PendingEvent): void {
  if (!pending.dedupeStorageKey) return;
  trackedOnceKeys.delete(pending.dedupeStorageKey);
  try { window.sessionStorage.removeItem(pending.dedupeStorageKey); } catch { /* Best effort. */ }
}

function clearPendingEvents(): void {
  pendingEvents.splice(0).forEach(discardPendingEvent);
}

function flushPendingEvents(): void {
  if (!hasAcceptedAnalyticsConsent() || !isBrowserAnalyticsEnvironmentAllowed() ||
      ["excluded_internal_or_qa", "unresolved"].includes(getAnalyticsAudience())) {
    clearPendingEvents();
    return;
  }
  const posthog = postHogIfLoaded();
  if (!isBrowserAnalyticsCaptureAllowed() || !posthog) return;
  while (pendingEvents.length > 0) {
    const pending = pendingEvents.shift();
    if (!pending) continue;
    try {
      posthog.capture(pending.event, sanitizeAnalyticsProps(withAnalyticsEnvironmentContext(pending.props)));
    } catch {
      discardPendingEvent(pending);
    }
  }
}

function observePendingEvents(): void {
  if (typeof window === "undefined" || observedWindow === window) return;
  unsubscribePermission?.();
  observedWindow?.removeEventListener("fairpath:posthog-ready", flushPendingEvents);
  observedWindow = window;
  let consentRevision = getAnalyticsConsentRevision();
  unsubscribePermission = subscribeAnalyticsPermission(() => {
    if (consentRevision !== getAnalyticsConsentRevision()) {
      clearPendingEvents();
      consentRevision = getAnalyticsConsentRevision();
    }
    flushPendingEvents();
  });
  window.addEventListener("fairpath:posthog-ready", flushPendingEvents);
}

function enqueueUntilReady(event: string, props?: AnalyticsProps, dedupeStorageKey?: string): void {
  if (pendingEvents.length >= MAX_PENDING_EVENTS) {
    const evicted = pendingEvents.shift();
    if (evicted) discardPendingEvent(evicted);
  }
  // Consent is checked before retention. No identity or raw property enters the queue.
  pendingEvents.push({ event, props: sanitizeAnalyticsProps(props), dedupeStorageKey });
  observePendingEvents();
}

function immediateCaptureDistinctId(): string {
  let sdkDistinctId: unknown;
  try {
    sdkDistinctId = postHogIfLoaded()?.get_distinct_id();
  } catch {
    // The shared selector supplies an analytics-only session pseudonym.
  }
  return safeBrowserTelemetryDistinctId(sdkDistinctId);
}

function captureImmediately(event: string, props: AnalyticsProps): boolean {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  if (
    !apiKey ||
    typeof window === "undefined" ||
    !isBrowserAnalyticsCaptureAllowed()
  ) {
    return false;
  }

  const distinctId = immediateCaptureDistinctId();

  const safeProps = sanitizeAnalyticsProps(props) ?? {};
  const payload = JSON.stringify({
    api_key: apiKey,
    event,
    distinct_id: distinctId,
    properties: {
      ...safeProps,
      $current_url: sanitizeAnalyticsValue(window.location.href),
      $pathname: sanitizeAnalyticsValue(window.location.pathname),
      analytics_transport: "immediate",
    },
  });

  try {
    if (navigator.sendBeacon) {
      const sent = navigator.sendBeacon(
        DIRECT_POSTHOG_ENDPOINT,
        new Blob([payload], { type: "application/json" })
      );
      if (sent) return true;
    }

    fetch(DIRECT_POSTHOG_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {});
    return true;
  } catch {
    // Analytics should never affect application behavior.
    return false;
  }
}

/**
 * Fire a named analytics event, or report that nothing was captured. Never
 * throws: returns false when PostHog is unavailable (ad blocker, missing API
 * key, network error) and when anything in the capture path fails.
 *
 * Analytics is best-effort, and several callers capture an event inside a
 * click handler and then advance the homeowner. A throw from anywhere in the
 * capture path escaped those handlers and cancelled the action with nothing
 * rendered (FAI-172), which reads to a homeowner as a dead control. Every
 * failure below the boundary is therefore a `false` return, not an exception.
 *
 * Swallowed, but not silent: a capture that fails is reported to Sentry, the
 * way every other best-effort path here reports. Without that, a sanitizer or
 * context change could zero out the funnel in production and nothing would
 * say so.
 */
function captureEvent(
  event: string,
  props?: AnalyticsProps,
  options?: AnalyticsTransportOptions,
  dedupeStorageKey?: string,
): boolean {
  try {
    return captureEventOrThrow(event, props, options, dedupeStorageKey);
  } catch (error) {
    reportCaptureFailure(event, error);
    return false;
  }
}

/** The event name only: props are caller-supplied and are not reported. */
function reportCaptureFailure(event: string, error: unknown): void {
  try {
    captureException(error instanceof Error ? error : new Error("Analytics capture failed"), {
      tags: { error_context: "analytics_capture" },
      level: "warning",
      extra: { analytics_event: event },
    });
  } catch {
    // Reporting the failure must never become the failure.
  }
  if (import.meta.env.DEV) console.warn(`[analytics] capture failed for ${event}`, error);
}

function captureEventOrThrow(
  event: string,
  props?: AnalyticsProps,
  options?: AnalyticsTransportOptions,
  dedupeStorageKey?: string,
): boolean {
  observePendingEvents();
  if (!hasAcceptedAnalyticsConsent() || !isBrowserAnalyticsEnvironmentAllowed()) {
    clearPendingEvents();
    return false;
  }
  const audience = getAnalyticsAudience();
  if (audience === "excluded_internal_or_qa" || audience === "unresolved") {
    clearPendingEvents();
    return false;
  }
  if (audience === "pending" || !isPostHogReady()) {
    // Navigation-sensitive events must not fall back to a delayed transport
    // while audience eligibility is unknown.
    if (options?.transport === "immediate" && audience === "pending") return false;
    if (options?.transport !== "immediate") {
      enqueueUntilReady(event, props, dedupeStorageKey);
      return true;
    }
  }
  const safeProps = sanitizeAnalyticsProps(withAnalyticsEnvironmentContext(props));
  if (options?.transport === "immediate" && captureImmediately(event, safeProps ?? {})) return true;
  // The SDK chunk is fetched lazily after consent. Until it is initialized
  // there is nothing to capture with, so keep the consented queue instead.
  const posthog = postHogIfLoaded();
  if (!posthog) {
    enqueueUntilReady(event, props, dedupeStorageKey);
    return true;
  }
  try {
    posthog.capture(event, safeProps);
    return true;
  } catch { return false; }
}

/** True means accepted for capture or a consented, bounded pending queue. */
export const track = (event: string, props?: AnalyticsProps, options?: AnalyticsTransportOptions): boolean =>
  captureEvent(event, props, options);

/**
 * Capture an event once per browser session and stable business key.
 *
 * This is intended for idempotent lifecycle events whose underlying operation
 * can legitimately be retried and return the same object (for example, Stripe
 * Checkout session reuse). The key is hashed before storage so raw provider
 * identifiers are not added to browser-storage key names.
 */
export function trackOnce(
  event: string,
  dedupeKey: string | null | undefined,
  props?: AnalyticsProps,
): boolean {
  if (!dedupeKey) {
    return track(event, props);
  }

  const storageKey = `${TRACK_ONCE_STORAGE_PREFIX}${hashAnalyticsKey(`${event}:${dedupeKey}`)}`;
  if (trackedOnceKeys.has(storageKey)) return false;
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      if (window.sessionStorage.getItem(storageKey) === "1") {
        trackedOnceKeys.add(storageKey);
        return false;
      }
    }
  } catch {
    // Storage can be disabled; in that case analytics remains best-effort.
  }

  if (!captureEvent(event, props, undefined, storageKey)) return false;
  trackedOnceKeys.add(storageKey);
  try { window.sessionStorage.setItem(storageKey, "1"); } catch { /* Memory dedupe remains. */ }
  return true;
}

function hashAnalyticsKey(value: string): string {
  let hash = 0xcbf29ce484222325n;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= BigInt(value.charCodeAt(index));
    hash = BigInt.asUintN(64, hash * 0x100000001b3n);
  }
  return hash.toString(16).padStart(16, "0");
}

export const __analyticsTestHooks = {
  sanitizeAnalyticsProps,
  flushPendingEvents,
  getPendingEventCount: () => pendingEvents.length,
  reset: () => {
    pendingEvents.length = 0;
    trackedOnceKeys.clear();
    unsubscribePermission?.();
    unsubscribePermission = undefined;
    observedWindow?.removeEventListener("fairpath:posthog-ready", flushPendingEvents);
    observedWindow = undefined;
  },
};
